/**
 * Printer Setup Wizard Component
 * 
 * Provides user-friendly UI for:
 * 1. Scanning available printers from Print Agent
 * 2. Testing selected printer
 * 3. Saving printer configuration
 * 
 * IMPORTANT: This wizard requires the Print Agent to be running on Windows.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert';
import { toast } from 'sonner';
import {
  Printer,
  Search,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  AlertCircle,
  Wifi,
  Cable,
  HelpCircle,
  Settings2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PrinterSetupWizard({ 
  outletId, 
  open, 
  onOpenChange, 
  onPrinterSaved,
  existingPrinter = null 
}) {
  const { api } = useAuth();
  
  // Wizard state
  const [step, setStep] = useState(1); // 1: Info, 2: Test, 3: Save
  const [loading, setLoading] = useState(false);
  
  // Agent status
  const [agentStatus, setAgentStatus] = useState(null);
  const [checkingAgent, setCheckingAgent] = useState(false);
  
  // Printer form
  const [printerName, setPrinterName] = useState('');
  const [printerRole, setPrinterRole] = useState('CASHIER');
  const [connectionType, setConnectionType] = useState('USB');
  const [ipAddress, setIpAddress] = useState('');
  const [port, setPort] = useState('9100');
  
  // Test status
  const [testStatus, setTestStatus] = useState(null); // null, 'pending', 'success', 'failed'
  const [testJobId, setTestJobId] = useState(null);
  const [testError, setTestError] = useState(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep(1);
      setTestStatus(null);
      setTestJobId(null);
      setTestError(null);
      
      if (existingPrinter) {
        setPrinterName(existingPrinter.name || '');
        setPrinterRole(existingPrinter.printer_role || 'CASHIER');
        setConnectionType(existingPrinter.connection_type || 'USB');
        setIpAddress(existingPrinter.ip_address || '');
        setPort(existingPrinter.port?.toString() || '9100');
      } else {
        setPrinterName('');
        setPrinterRole('CASHIER');
        setConnectionType('USB');
        setIpAddress('');
        setPort('9100');
      }
      
      checkAgentStatus();
    }
  }, [open, existingPrinter]);

  // Check Print Agent status
  const checkAgentStatus = async () => {
    if (!outletId) return;
    
    setCheckingAgent(true);
    try {
      const response = await fetch(`${API_URL}/api/print/agent/status?outlet_id=${outletId}`);
      if (response.ok) {
        const data = await response.json();
        setAgentStatus(data);
      } else {
        setAgentStatus({ status: 'ERROR', message: 'Gagal menghubungi server' });
      }
    } catch (error) {
      setAgentStatus({ status: 'ERROR', message: 'Tidak dapat terhubung ke server' });
    } finally {
      setCheckingAgent(false);
    }
  };

  // Send test print
  const sendTestPrint = async () => {
    if (!printerName.trim()) {
      toast.error('Nama printer harus diisi');
      return;
    }
    
    setTestStatus('pending');
    setTestError(null);
    
    try {
      // First, save or update the printer
      const printerData = {
        name: printerName.trim(),
        printer_role: printerRole,
        connection_type: connectionType,
        ip_address: connectionType === 'LAN' ? ipAddress : null,
        port: connectionType === 'LAN' ? parseInt(port) || 9100 : 9100,
        outlet_id: outletId,
        is_active: true,
      };
      
      let printerId;
      
      if (existingPrinter) {
        // Update existing printer
        await api.put(`/printers/${existingPrinter.id}`, printerData);
        printerId = existingPrinter.id;
      } else {
        // Create new printer temporarily
        const createResp = await api.post('/printers', printerData);
        printerId = createResp.data.printer?.id;
      }
      
      if (!printerId) {
        throw new Error('Gagal menyimpan konfigurasi printer');
      }
      
      // Now send test print via agent endpoint
      const testResp = await fetch(
        `${API_URL}/api/printers/agent/test?printer_id=${printerId}&outlet_id=${outletId}`,
        { method: 'POST' }
      );
      
      if (!testResp.ok) {
        const errData = await testResp.json();
        throw new Error(errData.detail || 'Gagal membuat test print job');
      }
      
      const testData = await testResp.json();
      setTestJobId(testData.job_id);
      
      // Start polling for test status
      pollTestStatus(testData.job_id, printerId);
      
    } catch (error) {
      console.error('Test print error:', error);
      setTestStatus('failed');
      setTestError(error.message || 'Gagal mengirim test print');
    }
  };

  // Poll test print status
  const pollTestStatus = async (jobId, printerId) => {
    const maxAttempts = 20; // 20 seconds max
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/printers/agent/test/${jobId}/status`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'PRINTED') {
            setTestStatus('success');
            toast.success('Test print berhasil!');
            return;
          } else if (data.status === 'FAILED') {
            setTestStatus('failed');
            setTestError(data.last_error || 'Print gagal');
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          // Timeout - agent might not be running
          setTestStatus('failed');
          setTestError('Timeout - Print Agent mungkin tidak berjalan atau printer offline');
        }
      } catch (error) {
        setTestStatus('failed');
        setTestError('Gagal memeriksa status test print');
      }
    };
    
    poll();
  };

  // Save printer configuration
  const savePrinter = async () => {
    setLoading(true);
    try {
      const printerData = {
        name: printerName.trim(),
        printer_role: printerRole,
        connection_type: connectionType,
        ip_address: connectionType === 'LAN' ? ipAddress : null,
        port: connectionType === 'LAN' ? parseInt(port) || 9100 : 9100,
        outlet_id: outletId,
        is_active: true,
      };
      
      if (existingPrinter) {
        await api.put(`/printers/${existingPrinter.id}`, printerData);
        toast.success('Printer berhasil diperbarui');
      } else {
        await api.post('/printers', printerData);
        toast.success('Printer berhasil ditambahkan');
      }
      
      onPrinterSaved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Gagal menyimpan printer');
    } finally {
      setLoading(false);
    }
  };

  const isAgentOnline = agentStatus?.status === 'ONLINE' || agentStatus?.status === 'PARTIAL';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="printer-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            {existingPrinter ? 'Edit Printer' : 'Setup Printer Baru'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Masukkan informasi printer thermal Anda'}
            {step === 2 && 'Verifikasi printer dengan test print'}
            {step === 3 && 'Simpan konfigurasi printer'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex items-center ${s > 1 ? 'ml-2' : ''}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  s === step
                    ? 'bg-primary text-primary-foreground'
                    : s < step
                    ? 'bg-green-500 text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {s < step ? <CheckCircle2 className="h-4 w-4" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-8 h-0.5 ${s < step ? 'bg-green-500' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Printer Info */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Agent Status Banner */}
            <Alert variant={isAgentOnline ? 'default' : 'destructive'}>
              <div className="flex items-center gap-2">
                {checkingAgent ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isAgentOnline ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <AlertTitle className="mb-0">
                  {checkingAgent
                    ? 'Memeriksa Print Agent...'
                    : isAgentOnline
                    ? 'Print Agent Terhubung'
                    : 'Print Agent Offline'}
                </AlertTitle>
              </div>
              <AlertDescription className="mt-1">
                {isAgentOnline ? (
                  <span className="text-green-600">{agentStatus?.message}</span>
                ) : (
                  <span>Pastikan Print Agent berjalan di komputer kasir untuk test print.</span>
                )}
              </AlertDescription>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={checkAgentStatus}
                disabled={checkingAgent}
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${checkingAgent ? 'animate-spin' : ''}`} />
                Refresh Status
              </Button>
            </Alert>

            {/* Printer Name */}
            <div>
              <Label htmlFor="printer-name">Nama Printer</Label>
              <Input
                id="printer-name"
                value={printerName}
                onChange={(e) => setPrinterName(e.target.value)}
                placeholder="Contoh: Epson TM-T82, POS-58 USB"
                data-testid="printer-name-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Masukkan nama persis seperti yang terlihat di Windows Devices
              </p>
            </div>

            {/* Printer Role */}
            <div>
              <Label>Fungsi Printer</Label>
              <Select value={printerRole} onValueChange={setPrinterRole}>
                <SelectTrigger data-testid="printer-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASHIER">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500">KASIR</Badge>
                      <span className="text-xs">Cetak struk pelanggan</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="KITCHEN">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-orange-500">KITCHEN</Badge>
                      <span className="text-xs">Cetak tiket dapur</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="BAR">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500">BAR</Badge>
                      <span className="text-xs">Cetak tiket bar</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Connection Type */}
            <div>
              <Label>Tipe Koneksi</Label>
              <Select value={connectionType} onValueChange={setConnectionType}>
                <SelectTrigger data-testid="connection-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USB">
                    <div className="flex items-center gap-2">
                      <Cable className="h-4 w-4" />
                      USB
                    </div>
                  </SelectItem>
                  <SelectItem value="LAN">
                    <div className="flex items-center gap-2">
                      <Wifi className="h-4 w-4" />
                      LAN / Network
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* LAN Settings */}
            {connectionType === 'LAN' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>IP Address</Label>
                  <Input
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    placeholder="192.168.1.100"
                    data-testid="ip-address-input"
                  />
                </div>
                <div>
                  <Label>Port</Label>
                  <Input
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder="9100"
                    data-testid="port-input"
                  />
                </div>
              </div>
            )}

            {/* Help Link */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <HelpCircle className="h-4 w-4" />
              <span>Lihat panduan lengkap di menu <strong>Help → Printer Setup</strong></span>
            </div>
          </div>
        )}

        {/* Step 2: Test Print */}
        {step === 2 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Printer className="h-4 w-4" />
                  Test Print
                </CardTitle>
                <CardDescription>
                  Kirim test print untuk memastikan printer terhubung dengan benar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  {/* Test Status Display */}
                  {testStatus === null && (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                        <Printer className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground">
                        Klik tombol di bawah untuk mengirim test print
                      </p>
                    </div>
                  )}

                  {testStatus === 'pending' && (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                      </div>
                      <p className="text-blue-600 font-medium">Menunggu hasil print...</p>
                      <p className="text-sm text-muted-foreground">
                        Pastikan Print Agent berjalan dan printer menyala
                      </p>
                    </div>
                  )}

                  {testStatus === 'success' && (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                        <CheckCircle2 className="h-8 w-8 text-green-500" />
                      </div>
                      <p className="text-green-600 font-medium">Test Print Berhasil!</p>
                      <p className="text-sm text-muted-foreground">
                        Printer "{printerName}" siap digunakan
                      </p>
                    </div>
                  )}

                  {testStatus === 'failed' && (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
                        <XCircle className="h-8 w-8 text-red-500" />
                      </div>
                      <p className="text-red-600 font-medium">Test Print Gagal</p>
                      <p className="text-sm text-destructive">{testError}</p>
                      <Alert variant="destructive" className="text-left mt-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Periksa hal berikut:</AlertTitle>
                        <AlertDescription>
                          <ul className="list-disc list-inside text-sm mt-2">
                            <li>Print Agent berjalan di komputer kasir</li>
                            <li>Nama printer sesuai dengan Windows</li>
                            <li>Printer menyala dan terhubung</li>
                            <li>Tidak ada paper jam</li>
                          </ul>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>

                {/* Test Button */}
                <Button
                  className="w-full"
                  onClick={sendTestPrint}
                  disabled={testStatus === 'pending' || !printerName.trim()}
                  data-testid="send-test-print-btn"
                >
                  {testStatus === 'pending' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {testStatus === 'pending' ? 'Menunggu...' : 
                   testStatus === 'success' ? 'Test Lagi' :
                   testStatus === 'failed' ? 'Coba Lagi' : 'Kirim Test Print'}
                </Button>
              </CardContent>
            </Card>

            {/* Skip Test Option */}
            {existingPrinter && (
              <p className="text-center text-sm text-muted-foreground">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setStep(3)}
                >
                  Lewati test dan simpan
                </Button>
              </p>
            )}
          </div>
        )}

        {/* Step 3: Confirm Save */}
        {step === 3 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Konfirmasi Konfigurasi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nama Printer</span>
                  <span className="font-medium">{printerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fungsi</span>
                  <Badge className={
                    printerRole === 'CASHIER' ? 'bg-green-500' :
                    printerRole === 'KITCHEN' ? 'bg-orange-500' : 'bg-blue-500'
                  }>
                    {printerRole === 'CASHIER' ? 'KASIR' : printerRole}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Koneksi</span>
                  <span className="font-medium">
                    {connectionType === 'USB' ? 'USB' : `LAN (${ipAddress}:${port})`}
                  </span>
                </div>
                {testStatus === 'success' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status Test</span>
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Berhasil
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Penting</AlertTitle>
              <AlertDescription>
                Pastikan Print Agent tetap berjalan di komputer kasir agar printer dapat mencetak otomatis.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={loading}
            >
              Kembali
            </Button>
          )}
          
          {step === 1 && (
            <Button
              onClick={() => setStep(2)}
              disabled={!printerName.trim() || (connectionType === 'LAN' && !ipAddress.trim())}
              data-testid="wizard-next-btn"
            >
              Lanjut ke Test Print
            </Button>
          )}
          
          {step === 2 && (
            <Button
              onClick={() => setStep(3)}
              disabled={testStatus === 'pending'}
              variant={testStatus === 'success' ? 'default' : 'outline'}
              data-testid="wizard-continue-btn"
            >
              {testStatus === 'success' ? 'Lanjut Simpan' : 'Lewati Test'}
            </Button>
          )}
          
          {step === 3 && (
            <Button
              onClick={savePrinter}
              disabled={loading}
              data-testid="wizard-save-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Simpan Printer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PrinterSetupWizard;
